/* eslint-disable @typescript-eslint/no-unused-vars */
import { Button, Icons, Row, Typography } from "@ster/ster-toolkit";
import { Col } from "antd";
import classNames from "classnames";
import { Moment } from "moment";
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useRecoilState, useRecoilValue, useResetRecoilState } from "recoil";

import {
  SterWebsiteModelPriceCompareCampaign,
  SterWebsiteModelPriceCompareSubCampaign,
} from "./api";
import {
  campaignsState,
  conversionGroupsState,
  mediumState,
  metadataState,
} from "./atoms";
import CampaignForm from "./CampaignForm";
import styles from "./Compare.module.less";
import Medium from "./Medium";
import Results from "./Results";
import ResultsLabels from "./ResultsLabels";
import SimpleSpinner from "./SimpleSpinner";
import SubCampaignForm from "./SubCampaignForm";

export interface SubCampaign extends SterWebsiteModelPriceCompareSubCampaign {
  id: number;
}

export interface Campaign
  extends Omit<SterWebsiteModelPriceCompareCampaign, "period"> {
  period: [Moment, Moment];
  subCampaigns: SubCampaign[];
}

const AddCampaign = ({
  onAddCampaign,
  medium,
  campaigns,
}: {
  onAddCampaign: () => void;
  medium: string;
  campaigns: Partial<Campaign>[];
}): JSX.Element | null => {
  const metadata = useRecoilValue(metadataState(medium));
  const isRadio = useMemo(() => medium === "radio", [medium]);
  return isRadio || campaigns.length < metadata.operators.length ? (
    <Col>
      <Button
        mode="tertiary"
        onClick={onAddCampaign}
        className={classNames(styles.add, {
          [styles.tv]: !isRadio,
        })}
      >
        + Vergelijk toevoegen
      </Button>
    </Col>
  ) : null;
};

const Compare = (): JSX.Element => {
  const [campaigns, setCampaigns] = useRecoilState(campaignsState);
  const [medium, _] = useRecoilState(mediumState);
  const resetCampaignsState = useResetRecoilState(campaignsState);
  const resetMediumState = useResetRecoilState(mediumState);
  const resetConversionGroupsState = useResetRecoilState(conversionGroupsState);
  const navRef = React.createRef<HTMLDivElement>();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const handleAddCampaign = useCallback(() => {
    const highestId =
      (campaigns?.length &&
        campaigns?.reduce((prev, curr) =>
          (prev.id ?? 0) > (curr.id ?? 0) ? prev : curr
        ).id) ??
      0;

    // Voeg een nieuwe campagne toe met een opgeteld id, kopieer de periode van de eerste campagne
    setCampaigns([
      ...campaigns,
      {
        id: highestId + 1,
        period: campaigns[0]?.period,
        subCampaigns: [{ id: 0 } as SubCampaign],
      },
    ]);
  }, [campaigns, setCampaigns]);

  const handleCampaignChange = useCallback(
    (campaign: Campaign) => {
      setCampaigns((currentCampaigns) => {
        const index = currentCampaigns.map(({ id }) => id).indexOf(campaign.id);

        // Als de eerste campagne wijzigt kopiëren we de periode naar de andere campagnes
        if (index === 0 && currentCampaigns.length > 1) {
          return [
            ...currentCampaigns
              .slice(0, index)
              .map((s) => ({ ...s, period: campaign.period })),
            { ...currentCampaigns[index], ...campaign },
            ...currentCampaigns
              .slice(index + 1)
              .map((s) => ({ ...s, period: campaign.period })),
          ];
        }

        return [
          ...currentCampaigns.slice(0, index),
          { ...currentCampaigns[index], ...campaign },
          ...currentCampaigns.slice(index + 1),
        ];
      });
    },
    [setCampaigns]
  );

  const handleDeleteCampaign = useCallback(
    (id: number) => {
      setCampaigns(campaigns.filter((s) => s.id !== id));
    },
    [campaigns, setCampaigns]
  );

  const resetMedium = useCallback(() => {
    resetCampaignsState();
    resetMediumState();
    resetConversionGroupsState();
  }, [resetCampaignsState, resetConversionGroupsState, resetMediumState]);

  const updateCanScroll = useCallback(() => {
    if (!navRef.current) {
      return;
    }
    const canScroll = navRef.current.scrollWidth > navRef.current.clientWidth;
    setCanScrollLeft(canScroll && navRef.current.scrollLeft !== 0);
    setCanScrollRight(
      canScroll &&
        navRef.current.scrollLeft + navRef.current.clientWidth <
          navRef.current.scrollWidth
    );
  }, [navRef]);

  const handleNav = useCallback(
    (direction) => {
      if (!navRef.current) {
        return;
      }

      if (direction === "left") {
        navRef.current.scrollLeft -= 315;
      } else {
        navRef.current.scrollLeft += 315;
      }
    },
    [navRef]
  );

  useEffect(() => {
    updateCanScroll();
  }, [updateCanScroll]);

  const maxSubCampaigns = useMemo(
    () => Math.max(...campaigns.map((c) => c.subCampaigns?.length ?? 0), 1),
    [campaigns]
  );

  return (
    <>
      <Row>
        <Typography.Title level={2}>Campagnevergelijker</Typography.Title>
        {medium && (
          <Button
            mode="tertiary"
            onClick={resetMedium}
            className={styles.reset}
          >
            Opnieuw beginnen
          </Button>
        )}
      </Row>
      {!medium ? (
        <Medium />
      ) : (
        <div className={styles.container}>
          <div className={styles.labels}>
            <CampaignForm
              onlyLabels
              campaign={{}}
              onChange={handleCampaignChange}
              onDelete={handleDeleteCampaign}
              canDelete={false}
              medium={medium}
            />
            {[...Array(maxSubCampaigns)].map((i, index) => (
              <SubCampaignForm
                onlyLabels
                subCampaign={{ id: i }}
                onChange={() => undefined}
                onDelete={() => undefined}
                canDelete={false}
                medium={medium}
                isFirst={index === 0}
                key={JSON.stringify(index)}
              />
            ))}
            <Suspense fallback={<SimpleSpinner />}>
              <ResultsLabels campaigns={campaigns} medium={medium} />
            </Suspense>
          </div>
          <button
            onClick={() => handleNav("left")}
            type="button"
            aria-label="Links"
            className={classNames("link", styles.scroll, styles.left)}
            hidden={!canScrollLeft}
          >
            <Icons.ChevronLeftIcon />
          </button>
          <div
            className={classNames(
              styles.scrollContainer,
              { [styles.shadowRight]: canScrollRight },
              { [styles.shadowLeft]: canScrollLeft }
            )}
            ref={navRef}
            onScroll={updateCanScroll}
          >
            <Row gutter={24} className={styles.campaigns}>
              {campaigns.map((campaign, index) => (
                <Col key={campaign.id}>
                  <CampaignForm
                    onlyLabels={false}
                    campaign={campaign}
                    unavailableOperators={campaigns
                      .map((s) => s.operator ?? "")
                      .filter((s) => s !== campaign.operator)}
                    onChange={handleCampaignChange}
                    onDelete={handleDeleteCampaign}
                    canDelete
                    medium={medium}
                    isSlave={index !== 0}
                  />
                </Col>
              ))}
              <AddCampaign
                medium={medium}
                onAddCampaign={handleAddCampaign}
                campaigns={campaigns}
              />
            </Row>

            <Row>
              {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
              <Suspense fallback={<></>}>
                <Results campaigns={campaigns} medium={medium} />
              </Suspense>
            </Row>
          </div>

          <button
            onClick={() => handleNav("right")}
            type="button"
            aria-label="Links"
            className={classNames("link", styles.scroll, styles.right)}
            hidden={!canScrollRight}
          >
            <Icons.ChevronRightIcon />
          </button>
        </div>
      )}
    </>
  );
};

export default Compare;
